import { createRoot } from "react-dom/client";
import { debug } from "utils/debug.utils";
import {
  APP_FONTS_REGISTER_STYLESHEET_HREF,
  APP_FONTS_STYLESHEET_HREF,
  APP_STYLESHEET_HREF,
  isAppIntegration,
} from "utils/url.utils";
import { AppInputRawData } from "./App.types";
import { APP_CONTAINER_CLASSNAME, APP_ROOT_CLASSNAME, POPUP_CLASSNAME } from "./constants/common.constants";
import { ShadowRootDom } from "./App.utils";
import { parseAppInputData } from "./App.utils";
import App from "./export";
import { parseJSONLikeString } from "./utils/common.utils";

const createInitialNodes = (): {
  appContainer: HTMLDivElement;
  appRoot: HTMLDivElement;
  modalNode: HTMLDivElement;
  appStylesheetNode: HTMLLinkElement;
  fontsRegisterStylesheetNode: HTMLLinkElement;
  fontsStylesheetNode: HTMLLinkElement;
} => {
  const appContainer = document.createElement("div");
  appContainer.className = APP_CONTAINER_CLASSNAME;
  appContainer.setAttribute("data-version", __APP_VERSION__);

  const appRoot = document.createElement("div");
  appRoot.className = APP_ROOT_CLASSNAME;

  const modalNode = document.createElement("div");
  modalNode.className = POPUP_CLASSNAME;

  const appStylesheetNode = document.createElement("link");
  appStylesheetNode.type = "text/css";
  appStylesheetNode.rel = "stylesheet";
  appStylesheetNode.href = APP_STYLESHEET_HREF;

  const fontsRegisterStylesheetNode = document.createElement("link");
  fontsRegisterStylesheetNode.type = "text/css";
  fontsRegisterStylesheetNode.rel = "stylesheet";
  fontsRegisterStylesheetNode.href = APP_FONTS_REGISTER_STYLESHEET_HREF;

  const fontsStylesheetNode = document.createElement("link");
  fontsStylesheetNode.type = "text/css";
  fontsStylesheetNode.rel = "stylesheet";
  fontsStylesheetNode.href = APP_FONTS_STYLESHEET_HREF;

  return {
    appContainer,
    appRoot,
    modalNode,
    appStylesheetNode,
    fontsRegisterStylesheetNode,
    fontsStylesheetNode,
  };
};

const main = () => {
  const rootNode: HTMLElement | null = document.getElementById("cargo_load_optimizer_root");
  if (!rootNode) return;

  const appInputRawData = parseJSONLikeString<AppInputRawData>(rootNode.dataset.filter);
  const appInputParsedData = parseAppInputData(appInputRawData);

  // delete all children from rootNode if shadowDom is false and app is not integrated
  if (!appInputParsedData.options.shadowDom && !isAppIntegration) {
    while (rootNode.firstChild) {
      rootNode.removeChild(rootNode.firstChild);
    }
  }

  const { appContainer, appRoot, modalNode, appStylesheetNode, fontsRegisterStylesheetNode, fontsStylesheetNode } =
    createInitialNodes();

  debug.setObject("appInputParsedData.shadowDom", appInputParsedData.options.shadowDom);
  debug.setObject("isAppIntegration", isAppIntegration);
  debug.setObject("useShadowDom", isAppIntegration || appInputParsedData.options.shadowDom);
  debug.setObject("dev-server-launched", __DEV_SERVER_LAUNCHED__);

  // Assets are only awaited so they are *settled*: a failure must not leave the
  // app unrendered. Listeners are attached before the nodes are inserted,
  // because an already-cached asset can otherwise settle before we start
  // listening and its load event would then never be observed.
  let appRendered = false;
  let stylesheetSettled = false;

  const renderApp = () => {
    createRoot(appRoot).render(<App inputData={appInputParsedData} />);
  };

  const tryRender = () => {
    if (appRendered || !stylesheetSettled) return;
    appRendered = true;
    debug.setObject("app-rendered", true);
    renderApp();
  };

  const settleStylesheet = () => {
    stylesheetSettled = true;
    tryRender();
  };

  if (!__DEV_SERVER_LAUNCHED__) {
    appStylesheetNode.addEventListener("load", settleStylesheet);
    appStylesheetNode.addEventListener("error", settleStylesheet);
  }

  rootNode.appendChild(fontsRegisterStylesheetNode);
  appContainer.appendChild(fontsStylesheetNode);
  ShadowRootDom.setAppContainer(appContainer);
  appContainer.appendChild(appRoot);
  appContainer.appendChild(modalNode);

  // Prod/build: attach the built CSS bundle. In Vite serve mode styles come from
  // <head> <style> tags; shadow-dom-css-bridge mirrors them into the shadow root.
  if (!__DEV_SERVER_LAUNCHED__) {
    appContainer.appendChild(appStylesheetNode);
  }

  // Embeddings on foreign hosts always use Shadow DOM (same as total-tracking).
  // Local HTML can opt in via data-filter shadowDom: true.
  if (isAppIntegration || appInputParsedData.options.shadowDom) {
    const shadowRootNode = rootNode.attachShadow({ mode: "open" });
    ShadowRootDom.setShadowRootNode(shadowRootNode);
    shadowRootNode.appendChild(appContainer);
  } else {
    rootNode.appendChild(appContainer);
  }

  if (__DEV_SERVER_LAUNCHED__) {
    stylesheetSettled = true;
  }

  tryRender();
};

if (__DEV_SERVER_LAUNCHED__) {
  main();
} else {
  (function checkForInit() {
    if (document.readyState === "complete") {
      return main();
    }

    debug.setObject(`document.readyState ${document.readyState}`, document.readyState);

    window.requestAnimationFrame(() => {
      checkForInit();
    });
  })();
}
